import React from 'react';
import {
    Table, Button, Col, FormGroup,
    UncontrolledTooltip, Row,
} from 'reactstrap';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import { v4 as uuid } from 'uuid';
import { isEmpty } from 'lodash-es';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faPaste, faPlus, faTimes, faArrowRightFromBracket,
    faCheck, faInfoCircle,
} from '@fortawesome/free-solid-svg-icons';
import CommonContext, {
    ChargeTypeResourceTypes, RentalUnitTypes,
    resourceHasChargeTypeChoice,
} from '../Common';
import {
    FormLabel, FormCheckbox, IconButton, FlexColumnStart,
    NumericInput, toasty, onFieldChange, FormGroupColumn,
    FormContext,
    FlexStartRow,
} from '../common/forms/FormElements';
import { DEFAULT_DATETIME_FORMAT } from '../common/util';
import { TipAlert } from '../common/TipAlert';
import ValidatedSelect from '../common/forms/ValidatedSelect';
import { ModalForm } from '../common/forms/ModalForm';

// Check if an AFAD resource has any assignment for the given day.
const doesAfadAssignmentExistForDay = (job, dayName) => {
    if (!job?.resources) return false;
    return job.resources.some((r) => r.isAFAD && Number(r[dayName]) > 0);
};

// Extracts the base resource name for planning purposes.
const getNonPlanningResourceName = (resourceName) => resourceName.startsWith('Flagger')
    ? resourceName.split(' ')[0]
    : null;

// Check if a resource has any assignment on any day.
const resourceHasAnyAssignments = (resource) =>
    Boolean(resource && ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday']
        .some((day) => Number(resource[day]) > 0));

// Check if a resource is a setup-charge row (ChargeTypeId also carries the selected
// charge type on equipment/rental rows, so the resource type is the discriminator).
const isSetupChargeRow = (resource) => resource?.jobResourceTypeId === ChargeTypeResourceTypes.Setup;

// Check if a resource is flagged as an AFAD resource.
const resourceIsAfad = (resource) => Boolean(
    resource && resource.isAFAD,
);

// Hydrates setup charges for a specific day.
const hydrateSetupCharges = (job, dayKey) => {
    if (!job?.resources?.length) return job;

    const hasEmployees = job.resources.some(
        (r) => !r.isPlanningOnly && r.jobResourceTypeId === ChargeTypeResourceTypes.Employee && Number(r[dayKey] || 0) > 0,
    );

    const resources = job.resources.map((r) =>
        isSetupChargeRow(r) && r.chargeTypeId > 0 && r.isNew ? { ...r, [dayKey]: hasEmployees ? 1 : 0 } : r,
    );
    return { ...job, resources };
};

/**
 * ResourcesPanel – full page section that includes:
 * - Resources table
 * - Requested Personnel (if available)
 * - Setup charges
 * - Permit fees
 */

export class ResourcePanel extends React.Component {
    static contextType = CommonContext;
    constructor(props) {
        super(props);
        this.state = {
            showAddSetupCharge: false,
            showAddPermitFees: false,
            showAddCertification: false,
            skippedResources: [],
        };

        this.onChange = this.onChange.bind(this);
    }

    onChange = onFieldChange;

    getResourceStatus = (dayKey, resourceName) => {
        const { edit } = { ...this.props };
        return edit?.dayStatuses?.[dayKey]?.resourceStatuses?.[resourceName] || null;
    };

    getPairedResourceCount = (resourceName, dayKey) => {
        const { job } = this.props;
        const { tenantSettings } = this.context.tenant;
        if (!tenantSettings || !tenantSettings.enableRequestedPersonnel) return null;

        // Get the base resource name (e.g., "Flaggers" from "Flaggers (Planning)")
        const baseName = getNonPlanningResourceName(resourceName);
        if (!baseName) return null;

        // Find an alternate resource whose name starts with the baseName but is not the same resource.
        const alternateResource = job.resources.find(
            (r) => r.resourceName !== resourceName && r.resourceName.startsWith(baseName),
        );
        if (!alternateResource) return null;

        return alternateResource[dayKey] || 0;
    };

    getResourceEditValidationMessage = (resourceName, newVal) => {
        const { selectedDate } = this.props;
        const dayKey = selectedDate.toLocaleString('en-us', { weekday: 'long' }).toLowerCase();

        let msg = null;
        // Get resource status details for this day and resource.
        const resourceStatus = this.getResourceStatus(dayKey, resourceName);
        if (resourceStatus?.statusCounts) {
            const maxStatus = resourceStatus.maxConfirmationStatusId;
            if (maxStatus < 2) return null; // No restrictions if maxStatus < 2

            // Determine a threshold based on existing assignments (status 2-4).
            const minCheckStatus = maxStatus === 2 ? 2 : 1;
            const threshold = Object.keys(resourceStatus.statusCounts).reduce((acc, statKey) => {
                const sKeyNum = parseInt(statKey, 10);
                if (sKeyNum >= minCheckStatus && sKeyNum <= 4) {
                    acc += resourceStatus.statusCounts[statKey];
                }
                return acc;
            }, 0);

            if (newVal < threshold) {
                const uppercaseDay = dayKey.charAt(0).toUpperCase() + dayKey.slice(1);
                msg = `"${resourceName}" must be at least ${threshold} on ${uppercaseDay} due to existing statuses.`;
                return msg;
            }
        }

        return msg;
    };

    // Updates the resource assignments for a specific day.
    updateJobResourcesForDay(resource, newQuantity) {
        const { selectedDate, edit, job } = this.props;
        const { showAddSetupCharge } = this.state;

        const dayKey = selectedDate.toLocaleString('en-us', { weekday: 'long' }).toLowerCase();

        let val = parseInt(newQuantity, 10);

        if (isNaN(val)) {
            val = 0;
        }

        if (val >= 1000) {
            toasty.error('Resource assignment cannot exceed 999.');
            return job;
        }

        // Validate new quantity against current assignments and thresholds.
        const errorMsg = this.getResourceEditValidationMessage(resource.resourceName, val);

        if (errorMsg) {
            toasty.error('Resource Update', errorMsg);
            // Save the error message in the edit object's resourceErrors.
            const npName = getNonPlanningResourceName(resource.resourceName) || resource.resourceName;
            edit.resourceErrors[`${npName}_${dayKey}`] = errorMsg;
            return job;
        } else {
            // Clear any previous errors for this resource.
            const npName = getNonPlanningResourceName(resource.resourceName) || resource.resourceName;
            delete edit.resourceErrors[`${npName}_${dayKey}`];
        }

        // Update the specific resource's assignment for the day.
        let updatedJob = {
            ...job,
            resources: job.resources.map((r) =>
                r.id === resource.id ? { ...r, [dayKey]: val } : r,
            ),
        };

        // For planning-only resources: if no AFAD assignment exists, reset their value.
        updatedJob = {
            ...updatedJob,
            resources: updatedJob.resources.map((r) =>
                r.isPlanningOnly && !doesAfadAssignmentExistForDay(updatedJob, dayKey)
                    ? { ...r, [dayKey]: 0 }
                    : r,
            ),
        };

        // If setup charges should be hydrated, update the job.
        if (showAddSetupCharge) {
            updatedJob = hydrateSetupCharges(updatedJob, dayKey);
        }


        return updatedJob;
    }

    handleQuantityChange = (resourceId, newQuantity) => {
        const { selectedDate, job } = this.props;

        if (!selectedDate) return;

        const dayKey = selectedDate.toLocaleString('en-us', { weekday: 'long' }).toLowerCase();
        const resource = job.resources.find((r) => r.id === resourceId);
        if (!resource) return;

        let updatedJob = this.updateJobResourcesForDay(resource, newQuantity);

        if (!resource.isPlanningOnly && resource.jobResourceTypeId === ChargeTypeResourceTypes.Employee) {
            updatedJob = hydrateSetupCharges(updatedJob, dayKey);
        }

        this.props.onJobChange(updatedJob);
    };

    handleChargeTypeChange = (resourceId, selection) => {
        const { job } = this.props;

        const updatedJob = {
            ...job,
            resources: job.resources.map((r) => r.id === resourceId ? {
                ...r,
                chargeTypeId: selection?.value ?? null,
            } : r),
        };

        this.props.onJobChange(updatedJob);
    };

    handlePickupRemainingEquipment = (resourceId) => {
        const { project, job } = this.props;

        const resource = job.resources.find((r) => r.id === resourceId);

        const projectResource = project.resources.find((r) => r.equipmentTypeId === resource.equipmentTypeId);

        this.handleRentalQuantityChange(resourceId, 'rentalPickupQuantity', projectResource?.actualQuantity || projectResource?.expectedQuantity || 0);
    };

    handleRentalQuantityChange = (resourceId, field, newQuantity) => {
        const { selectedDate, job } = this.props;

        if (!selectedDate) return job;

        let val = parseInt(newQuantity, 10);
        if (isNaN(val)) {
            val = 0;
        }
        if (val >= 1000) {
            toasty.error('Resource assignment cannot exceed 999.');
            return job;
        }

        // 1) get the date you want to default to when qty goes > 0
        const defaultDate = this.getDefaultRentalDate();
        // 2) normalize the incoming quantity to a number
        const quantity = parseFloat(newQuantity) || 0;

        const dayKey = selectedDate.toLocaleString('en-us', { weekday: 'long' }).toLowerCase();

        const resource = job.resources.find((r) => r.id === resourceId);

        if (!resource) return job; // Ensure resource exists before proceeding

        // Set the resource's rental start date to the default date if it's not set

        // Determine the complementary field (pickup or dropoff) and its current value
        const complementaryField = field === 'rentalDropoffQuantity' ? 'rentalPickupQuantity' : 'rentalDropoffQuantity';
        const complementaryQuantity = parseFloat(resource[complementaryField] || 0);

        // If both dropoff and pickup quantities are zero, clear the day's assignment and dates
        if (quantity === 0 && complementaryQuantity === 0) {
            const updatedJob = {
                ...job,
                resources: job.resources.map((resourceItem) => resourceItem.id === resourceId ? {
                    ...resourceItem,
                    rentalDropoffQuantity: 0,
                    rentalPickupQuantity: 0,
                    [dayKey]: 0, // Clear the assignment for this day
                    rentalStartDate: null, // Reset rental start date
                    rentalEndDate: null, // Reset rental end date
                } : resourceItem),
            };
            this.props.onJobChange(updatedJob);

            return updatedJob;
        }

        // Update the specific resource's assignment for the day.
        let resourceToUpdate = job.resources.find((r) => r.id === resource.id);

        let dayQuantity = parseFloat(resource[dayKey] || (newQuantity > 0 ? 1 : 0));

        if (!resourceToUpdate.equipmentTypeIsGeneric) {
            dayQuantity = parseFloat(newQuantity);

            // The total quantity of the day should equal both dropoffs AND pickups
            if (field === 'rentalDropoffQuantity') {
                dayQuantity += parseFloat(resourceToUpdate.rentalPickupQuantity || 0);
            } else if (field === 'rentalPickupQuantity') {
                dayQuantity += parseFloat(resourceToUpdate.rentalDropoffQuantity || 0);
            }
        }

        resourceToUpdate = {
            ...resourceToUpdate,
            [field]: quantity,
            [dayKey]: dayQuantity,
        };

        if (field === 'rentalDropoffQuantity') {
            resourceToUpdate.rentalStartDate = quantity > 0
                ? (resourceToUpdate.rentalStartDate || defaultDate)
                : null;
        } else if (field === 'rentalPickupQuantity') {
            resourceToUpdate.rentalEndDate = quantity > 0
                ? (resourceToUpdate.rentalEndDate || defaultDate)
                : null;
        }

        const indexToUpdate = job.resources.findIndex((r) => r.id === resourceToUpdate.id);
        job.resources[indexToUpdate] = resourceToUpdate;

        this.props.onJobChange(job);
        return job;
    };

    handleRentalDateChange = (resourceId, field, date) => {
        const { job } = this.props;
        const resource = job.resources.find((r) => r.id === resourceId);

        if (!resource) return; // Ensure resource exists before proceeding

        // Update the specific resource's assignment for the day.
        const updatedJob = {
            ...job,
            resources: job.resources.map((r) =>
                r.id === resource.id ? { ...r, [field]: moment(date).format(DEFAULT_DATETIME_FORMAT) } : r,
            ),
        };

        this.props.onJobChange(updatedJob);
    };

    applySameRentalStartDateToAll = () => {
        const { job } = this.props;
        const { resources } = job;

        const firstResourceWithStartDate = resources.find((r) => r.rentalDropoffQuantity > 0 && r.rentalStartDate !== null);

        if (firstResourceWithStartDate) {
            const { rentalStartDate } = firstResourceWithStartDate;

            // Update the specific resource's assignment for the day.
            const updatedJob = {
                ...job,
                resources: job.resources.map((r) => ({
                    ...r,
                    rentalStartDate: r.rentalDropoffQuantity > 0 ? rentalStartDate : null,
                })),
            };

            this.props.onJobChange(updatedJob);
        }
    };

    applySameRentalEndDateToAll = () => {
        const { job } = this.props;
        const { resources } = job;

        const firstResourceWithEndDate = resources.find((r) => r.rentalPickupQuantity > 0 && r.rentalEndDate !== null);

        if (firstResourceWithEndDate) {
            const { rentalEndDate } = firstResourceWithEndDate;

            // Update the specific resource's assignment for the day.
            const updatedJob = {
                ...job,
                resources: job.resources.map((r) => ({
                    ...r,
                    rentalEndDate: r.rentalPickupQuantity > 0 ? rentalEndDate : null,
                })),
            };

            this.props.onJobChange(updatedJob);
        }
    };

    getDefaultRentalDate() {
        const { selectedDate, job: { startTime } } = this.props;
        const m = moment(selectedDate).clone();

        if (startTime) {
            const [hours, minutes] = startTime.split(':').map(Number);
            if (!Number.isNaN(hours) && !Number.isNaN(minutes)) {
                m.set({ hour: hours, minute: minutes });
            } else {
                const parsed = moment(startTime);
                if (parsed.isValid()) {
                    m.set({ hour: parsed.hour(), minute: parsed.minute() });
                }
            }
        } else {
            m.startOf('day');
        }

        return m.format(DEFAULT_DATETIME_FORMAT);
    }

    toggleAllSetupCharges = (checked) => {
        const { job } = this.props;

        const updatedJob = {
            ...job,
            resources: (job.resources ?? []).map((r) =>
                isSetupChargeRow(r) && r.chargeTypeId > 0 && !r.isPermit ? { ...r, isNew: checked } : r,
            ),
        };

        this.props.onJobChange(updatedJob);
    };

    onAddSetupCharge = () => {
        const { selectedDate, job } = this.props;
        const dayKey = selectedDate
            .toLocaleString('en-us', { weekday: 'long' })
            .toLowerCase();

        // Re-hydrate the job to assign quantity=1 if flaggers > 0
        const updatedJob = hydrateSetupCharges(job, dayKey);

        this.setState({ showAddSetupCharge: false });
        this.props.onJobChange(updatedJob);
    };

    removeSetupCharge = (resource) => {
        const { selectedDate, job } = this.props;
        const dayKey = selectedDate.toLocaleString('en-us', { weekday: 'long' }).toLowerCase();

        // Update the job resources
        const updatedResources = job.resources.map((r) =>
            r.id === resource.id ? { ...r, isNew: false, [dayKey]: 0 } : r,
        );

        // Rehydrate setup charges after removal
        const updatedJob = hydrateSetupCharges({ ...job, resources: updatedResources }, dayKey);

        this.props.onJobChange(updatedJob);
    };

    onAddPermitFees = () => {
        const { selectedDate, job } = this.props;
        const dayKey = selectedDate
            .toLocaleString('en-us', { weekday: 'long' })
            .toLowerCase();

        // Re-hydrate the job to assign quantity=1 if flaggers > 0
        const updatedJob = hydrateSetupCharges(job, dayKey);

        this.setState({ showAddPermitFees: false });
        this.props.onJobChange(updatedJob);
    };

    handlePermitFeesApplyChange = () => {
        const { job } = this.props;
        job.permitFeesApply = !job.permitFeesApply;
        this.props.onJobChange(job);
    };

    isRequestedPersonnelSectionAvailable() {
        const { job } = this.props;
        return (
            job?.resources &&
            job?.resources.some((r) => r.isPlanningOnly) &&
            job?.resources.some((r) => resourceIsAfad(r) && resourceHasAnyAssignments(r))
        );
    }

    onAddCertification = () => {
        this.setState({ showAddCertification: false });
    };

    removeCertification = (resource) => {
        const { selectedDate, job } = this.props;
        const dayKey = selectedDate.toLocaleString('en-us', { weekday: 'long' })
            .toLowerCase();

        job.resources = job.resources.map((r) =>
            r.id === resource.id
                ? { ...r, isNew: false, [dayKey]: 0 }
                : r,
        );

        this.props.onJobChange(job);
    };

    onSelectResourceKit = (selectedResourceKits) => {
        const {
            resourceKits, selectedDate, job, skippedResources,
        } = this.props;

        const dayKey = selectedDate
            .toLocaleDateString('en-US', { weekday: 'long' })
            .toLowerCase();

        const selectedIds = selectedResourceKits?.map((k) => k.value);
        const removedKitsIds = job.selectedResourceKits?.filter((id) => !selectedIds?.includes(id));

        const addedKits = selectedResourceKits.filter((k) => !job.selectedResourceKits?.includes(k.value));
        const removedKits = resourceKits?.filter((k) => removedKitsIds?.includes(k.value));

        const skipped = skippedResources ?? [];
        let updatedJob = job;
        // Add new resources quantities
        addedKits?.forEach((resourceKit) => {
            resourceKit.resources.forEach((rs) => {
                const jobResource = job.resources?.find((jrs) => jrs.typeName == rs.resourceTypeName
                    && (jrs.applicationGroupId ?? jrs.applicationUserGroupId ?? jrs.equipmentTypeId ?? jrs.chargeTypeId) == rs.resourceId);
                if (jobResource != null) {
                    if (jobResource.jobResourceTypeId == ChargeTypeResourceTypes.Rental) {
                        updatedJob = this.handleRentalQuantityChange(jobResource.id, 'rentalDropoffQuantity', (jobResource.rentalDropoffQuantity || 0) + rs.quantity);
                        jobResource.rentalDropoffQuantity = (jobResource.rentalDropoffQuantity || 0) + rs.quantity;
                    } else {
                        this.handleQuantityChange(jobResource.id, (jobResource[dayKey] || 0) + rs.quantity);
                        jobResource[dayKey] = (jobResource[dayKey] || 0) + rs.quantity;
                    }
                } else if (!skipped.includes(`${rs.resourceTypeName} ${rs.resourceName}`)) {
                    // Show info if some resources were skipped
                    skipped.push(`${rs.resourceTypeName} ${rs.resourceName}`);
                }
            });
        });

        // Substruct removed resources quantities
        removedKits?.forEach((resourceKit) => {
            resourceKit.resources.forEach((rs) => {
                const jobResource = job.resources?.find((jrs) => jrs.typeName == rs.resourceTypeName
                    && (jrs.applicationGroupId ?? jrs.applicationUserGroupId ?? jrs.equipmentTypeId ?? jrs.chargeTypeId) == rs.resourceId);
                if (jobResource != null) {
                    if (jobResource.jobResourceTypeId == ChargeTypeResourceTypes.Rental) {
                        updatedJob = this.handleRentalQuantityChange(jobResource.id, 'rentalDropoffQuantity', Math.max((jobResource.rentalDropoffQuantity || 0) - rs.quantity, 0));
                        jobResource.rentalDropoffQuantity = Math.max((jobResource.rentalDropoffQuantity || 0) - rs.quantity, 0);
                    } else {
                        this.handleQuantityChange(jobResource.id, Math.max((jobResource[dayKey] || 0) - rs.quantity, 0));
                        jobResource[dayKey] = Math.max((jobResource[dayKey] || 0) - rs.quantity, 0);
                    }
                }
            });
        });

        this.props.onJobChange({
            ...updatedJob,
            selectedResourceKits: selectedIds,
        });

        this.setState({
            skippedResources: skipped,
        });
    };

    render() {
        const {
            job, contract, selectedDate, edit, errors, tableRequired,
            selectedResourceKits, resourceKits, spanning = false, isRentalContract,
        } = this.props;

        const isQuickRentalContract = Boolean(contract?.isQuickRentalContract);

        const {
            showAddSetupCharge, showAddPermitFees, showAddCertification, skippedResources,
        } = this.state;

        const unusedCertifications = (job?.resources || []).filter((r) => r.certificationId > 0 && !r.isPermit);
        const unusedSetupChargeTypes = (job?.resources || []).filter((r) => isSetupChargeRow(r) && r.chargeTypeId > 0 && !r.isPermit);
        const unusedPermitFees = (job?.resources || []).filter((r) => isSetupChargeRow(r) && r.chargeTypeId > 0 && r.isPermit);
        const scTotal = (unusedSetupChargeTypes ?? []).length;
        const scSelected = (unusedSetupChargeTypes ?? []).filter((c) => c.isNew === true).length;
        const scAll = scTotal > 0 && scSelected === scTotal;
        const scCta = scAll ? 'Clear all' : 'Select all';
        const showResourceKits = !isQuickRentalContract && ((typeof job?.id === 'string' && Boolean(resourceKits) && resourceKits.length > 0) || isRentalContract);
        // Add a FormContext here to make sure this is unaffected by parent contexts
        return (
            <FormContext.Provider value={null}>
                {showResourceKits && (
                    <Row>
                        {typeof job?.id === 'string' && Boolean(resourceKits) && resourceKits.length > 0 && (
                            <FormGroupColumn>
                                <ValidatedSelect
                                    id="resourceKit"
                                    name="selectedResourceKits"
                                    label="Resource Kit"
                                    isMulti
                                    options={resourceKits}
                                    value={
                                        (resourceKits ?? []).filter((x) => (
                                            job.selectedResourceKits ?? []
                                        ).includes(x.value)) ?? ''
                                    }
                                    formatOptionLabel={(opt, { context }) => {
                                        if (context === 'menu') {
                                            return (
                                                <FlexColumnStart>
                                                    <span>{opt.label}</span>
                                                    {Boolean(opt.tooltip) && (
                                                        <small className="text-muted mt-1">
                                                            {opt.tooltip}
                                                        </small>
                                                    )}
                                                </FlexColumnStart>
                                            );
                                        }

                                        return <span>{opt.label}</span>;
                                    }}
                                    onChange={this.onSelectResourceKit}
                                />

                                {(job.selectedResourceKits ?? []).length > 0 && (
                                    <TipAlert
                                        tipText="Note"
                                        label="Job resource quantities have been updated based on the selected resource kit(s). Please review the changes before saving the job."
                                    />
                                )}

                            </FormGroupColumn>
                        )}

                    </Row>
                )}

                {/* Resources Table */}
                <Row>
                    <Col>
                        <FormGroup>
                            <FormLabel
                                htmlFor="resources"
                                label="Resources"
                                required
                            />
                            <div
                                style={tableRequired}
                                className="table-overflow-visible"
                            >
                                <Table
                                    bordered
                                    responsive
                                    size="sm"
                                    className="mb-0 bg-white"
                                >
                                    <thead>
                                        <tr>
                                            <th>Resource</th>
                                            <th>Type</th>
                                            <th colSpan={contract?.isProjectContract ? 1 : 2}>Qty</th>

                                            {Boolean(contract?.isProjectContract) && (
                                                <>
                                                    <th>Qty Picked Up</th>
                                                    <th>Rental Start</th>
                                                    <th colSpan={2}>Rental End</th>
                                                </>
                                            )}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {!job?.resources?.length ? (
                                            <tr>
                                                <td
                                                    colSpan={4}
                                                    className="text-center"
                                                >
                                                    No resources assigned.
                                                </td>
                                            </tr>
                                        ) : (
                                            job?.resources
                                                .filter((r) => {
                                                    if (r.isPlanningOnly) return false;

                                                    if (
                                                        r.jobResourceTypeId === ChargeTypeResourceTypes.Rental &&
                                                        !contract?.isProjectContract
                                                    ) return false;

                                                    if (
                                                        isQuickRentalContract &&
                                                        r.jobResourceTypeId === ChargeTypeResourceTypes.Employee
                                                    ) return false;

                                                    if (!isSetupChargeRow(r) && !r.certificationId) return true;

                                                    if (isSetupChargeRow(r)) {
                                                        return r.isNew === true || resourceHasAnyAssignments(r);
                                                    }

                                                    if (r.certificationId > 0) {
                                                        return r.isNew === true || resourceHasAnyAssignments(r);
                                                    }

                                                    return false;
                                                })
                                                .map((resource) => {
                                                    const dayKey = selectedDate?.toLocaleString('en-us', { weekday: 'long' }).toLowerCase();
                                                    const quantity = resource[dayKey] || 0;
                                                    const dayKeyHasTimesheet = `${dayKey}HasTimesheet`;
                                                    const isDisabled = !spanning && (resource[dayKeyHasTimesheet] || (resource.isPlanningOnly && !doesAfadAssignmentExistForDay(job, dayKey)));

                                                    const isActionable = isSetupChargeRow(resource) || resource.certificationId > 0;

                                                    const rentalLocked =
                                                        resource.jobResourceTypeId === ChargeTypeResourceTypes.Rental &&
                                                        Boolean(resource[`${dayKey}HasAssignment`]);

                                                    const chargeTypeOptions = (resource.chargeTypeOptions ?? []).map((o) => ({
                                                        value: o.chargeTypeId,
                                                        label: o.description,
                                                    }));
                                                    const chargeTypeLocked = chargeTypeOptions.some((o) => o.value === resource.chargeTypeId)
                                                        && (['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']
                                                            .some((day) => resource[`${day}HasTimesheet`] === true)
                                                            || !edit.allowResources);

                                                    return (
                                                        <tr key={resource.id}>
                                                            <td
                                                                className="align-middle"
                                                                style={resourceHasChargeTypeChoice(resource) ? { minWidth: '200px' } : undefined}
                                                            >
                                                                {resourceHasChargeTypeChoice(resource) ? (
                                                                    <ValidatedSelect
                                                                        id={`resourceChargeType_${resource.id}`}
                                                                        name={`resourceChargeType_${resource.id}`}
                                                                        placeholder={resource.resourceName}
                                                                        options={chargeTypeOptions}
                                                                        value={chargeTypeOptions.find((o) => o.value === resource.chargeTypeId) ?? ''}
                                                                        onChange={(selection) => this.handleChargeTypeChange(resource.id, selection)}
                                                                        isDisabled={chargeTypeLocked}
                                                                        hideClear
                                                                    />
                                                                ) : resource.resourceName}
                                                                {rentalLocked && (
                                                                    <>
                                                                        <FontAwesomeIcon
                                                                            id={`rental-locked-${resource.id}`}
                                                                            icon={faInfoCircle}
                                                                            className="ml-1 text-info cursor-pointer"
                                                                        />
                                                                        <UncontrolledTooltip
                                                                            target={`rental-locked-${resource.id}`}
                                                                        >
                                                                            Remove assignments before changing quantity/dates
                                                                        </UncontrolledTooltip>
                                                                    </>
                                                                )}
                                                            </td>
                                                            <td className="align-middle">{resource.typeName}</td>

                                                            {resource.jobResourceTypeId !== ChargeTypeResourceTypes.Rental ? (
                                                                <>
                                                                    <td>
                                                                        <NumericInput
                                                                            min={0}
                                                                            step={1}
                                                                            wholeNumber
                                                                            style={{ minWidth: '55px' }}
                                                                            value={quantity}
                                                                            onChange={(e) => this.handleQuantityChange(resource.id, e.target.value)}
                                                                            disabled={
                                                                                isDisabled ||
                                                                                !edit.allowResources ||
                                                                                isSetupChargeRow(resource)
                                                                            }
                                                                            title={
                                                                                resource[dayKeyHasTimesheet]
                                                                                    ? 'Approved Timesheet Exists'
                                                                                    : ''
                                                                            }
                                                                        />
                                                                    </td>

                                                                    <td colSpan={isActionable ? 3 : 4} />
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <td>
                                                                        <NumericInput
                                                                            min={0}
                                                                            step={1}
                                                                            wholeNumber
                                                                            value={resource.rentalDropoffQuantity}
                                                                            onChange={(e) => this.handleRentalQuantityChange(resource.id, 'rentalDropoffQuantity', e.target.value)}
                                                                            disabled={
                                                                                isDisabled ||
                                                                                !edit.allowResources ||
                                                                                isSetupChargeRow(resource) ||
                                                                                rentalLocked
                                                                            }
                                                                            title={
                                                                                resource[dayKeyHasTimesheet]
                                                                                    ? 'Approved Timesheet Exists'
                                                                                    : ''
                                                                            }
                                                                        />
                                                                    </td>

                                                                    <td>
                                                                        <NumericInput
                                                                            min={0}
                                                                            step={1}
                                                                            wholeNumber
                                                                            value={resource.rentalPickupQuantity}
                                                                            onChange={(e) => this.handleRentalQuantityChange(resource.id, 'rentalPickupQuantity', e.target.value)}
                                                                            disabled={
                                                                                isDisabled ||
                                                                                !edit.allowResources ||
                                                                                isSetupChargeRow(resource) ||
                                                                                rentalLocked
                                                                            }
                                                                            title={
                                                                                resource[dayKeyHasTimesheet]
                                                                                    ? 'Approved Timesheet Exists'
                                                                                    : ''
                                                                            }
                                                                            endAddon={(
                                                                                <>
                                                                                    <IconButton
                                                                                        id={`pickup-remaining-${resource.id}`}
                                                                                        disabled={rentalLocked}
                                                                                        onClick={() => this.handlePickupRemainingEquipment(resource.id)}
                                                                                    >
                                                                                        <FontAwesomeIcon
                                                                                            icon={faArrowRightFromBracket}
                                                                                        />
                                                                                    </IconButton>

                                                                                    <UncontrolledTooltip
                                                                                        target={`pickup-remaining-${resource.id}`}
                                                                                    >
                                                                                        Pick up all remaining equipment
                                                                                    </UncontrolledTooltip>
                                                                                </>
                                                                            )}
                                                                        />
                                                                    </td>

                                                                    <td>
                                                                        <DatePicker
                                                                            className="form-control"
                                                                            popperPlacement="top-end"
                                                                            name="rentalStartDate"
                                                                            selected={resource.rentalStartDate ? moment(resource.rentalStartDate).toDate() : ''}
                                                                            onChange={(date) => this.handleRentalDateChange(resource.id, 'rentalStartDate', date)}
                                                                            selectsStart
                                                                            showTimeInput={resource.rentalUnitTypeId === RentalUnitTypes.Hourly}
                                                                            timeInputLabel="Time: "
                                                                            timeFormat={resource.rentalUnitTypeId === RentalUnitTypes.Hourly ? 'p' : undefined}
                                                                            dateFormat={resource.rentalUnitTypeId === RentalUnitTypes.Hourly ? 'Pp' : undefined}
                                                                            startDate={new Date()}
                                                                            disabled={
                                                                                isDisabled ||
                                                                                !edit.allowResources ||
                                                                                isSetupChargeRow(resource) ||
                                                                                resource.rentalDropoffQuantity <= 0 ||
                                                                                rentalLocked
                                                                            }
                                                                        />
                                                                    </td>

                                                                    <td colSpan={isActionable ? 1 : 2}>
                                                                        <DatePicker
                                                                            className="form-control"
                                                                            name="rentalEndDate"
                                                                            selected={resource.rentalEndDate ? moment(resource.rentalEndDate).toDate() : ''}
                                                                            onChange={(date) => this.handleRentalDateChange(resource.id, 'rentalEndDate', date)}
                                                                            selectsStart
                                                                            showTimeInput={resource.rentalUnitTypeId === RentalUnitTypes.Hourly}
                                                                            timeInputLabel="Time: "
                                                                            timeFormat={resource.rentalUnitTypeId === RentalUnitTypes.Hourly ? 'p' : undefined}
                                                                            dateFormat={resource.rentalUnitTypeId === RentalUnitTypes.Hourly ? 'Pp' : undefined}
                                                                            startDate={new Date()}
                                                                            disabled={
                                                                                isDisabled ||
                                                                                !edit.allowResources ||
                                                                                isSetupChargeRow(resource) ||
                                                                                resource.rentalPickupQuantity <= 0 ||
                                                                                rentalLocked
                                                                            }
                                                                        />
                                                                    </td>
                                                                </>
                                                            )}

                                                            {isActionable && (
                                                                <td
                                                                    className="text-center align-middle"
                                                                    style={{ width: '50px' }}
                                                                >
                                                                    {isSetupChargeRow(resource) && (
                                                                        <IconButton
                                                                            title="Remove setup charge"
                                                                            onClick={() =>
                                                                                this.removeSetupCharge(resource)
                                                                            }
                                                                        >
                                                                            <FontAwesomeIcon
                                                                                icon={faTimes}
                                                                                className="text-danger cursor-pointer"
                                                                            />
                                                                        </IconButton>
                                                                    )}

                                                                    {resource.certificationId > 0 && (
                                                                        <IconButton
                                                                            title="Remove certification"
                                                                            onClick={() =>
                                                                                this.removeCertification(resource)
                                                                            }
                                                                        >
                                                                            <FontAwesomeIcon
                                                                                icon={faTimes}
                                                                                className="text-danger cursor-pointer"
                                                                            />
                                                                        </IconButton>
                                                                    )}
                                                                </td>
                                                            )}
                                                        </tr>
                                                    );
                                                })
                                        )}
                                    </tbody>

                                    {!isEmpty(tableRequired) && errors.resources && (
                                        <tr>
                                            <td colSpan={contract?.isProjectContract ? 7 : 4}>
                                                <div className="ml-2">
                                                    <small className="text-danger">
                                                        {errors.resources}
                                                    </small>
                                                </div>
                                            </td>
                                        </tr>
                                    )}

                                    {contract?.isProjectContract && (
                                        <tfoot>
                                            <tr>
                                                <td colSpan={4} />

                                                <td align="left">
                                                    <Button
                                                        color="link"
                                                        type="button"
                                                        size="sm"
                                                        onClick={this.applySameRentalStartDateToAll}
                                                    >
                                                        <FontAwesomeIcon
                                                            icon={faPaste}
                                                            className="mr-1"
                                                        />
                                                        Use same for all
                                                    </Button>
                                                </td>

                                                <td
                                                    align="left"
                                                    colSpan={2}
                                                >
                                                    <Button
                                                        color="link"
                                                        type="button"
                                                        size="sm"
                                                        onClick={this.applySameRentalEndDateToAll}
                                                    >
                                                        <FontAwesomeIcon
                                                            icon={faPaste}
                                                            className="mr-1"
                                                        />
                                                        Use same for all
                                                    </Button>
                                                </td>
                                            </tr>
                                        </tfoot>
                                    )}

                                </Table>
                                {showResourceKits && (selectedResourceKits ?? []).length > 0 && (skippedResources ?? []).length > 0 && (
                                    <TipAlert
                                        tipText="Note"
                                        label={`The following resources quantities were not updated because they are not part of the job contract: ${skippedResources.join(', ')}`}
                                    />
                                )}
                            </div>
                        </FormGroup>
                    </Col>
                </Row>

                {this.isRequestedPersonnelSectionAvailable() && (
                    <Row>
                        <Col>
                            <FormGroup className="mb-0">
                                <FormLabel
                                    label="Requested Personnel"
                                    required
                                />
                                <div>
                                    <Table
                                        bordered
                                        responsive
                                        size="sm"
                                    >
                                        <thead>
                                            <tr>
                                                <th>Resource</th>
                                                <th>Type</th>
                                                <th>Assigned Days</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {
                                                job?.resources
                                                    .filter((r) => r.isPlanningOnly)
                                                    .map((resource) => {
                                                        const dayKey = selectedDate?.toLocaleString('en-us', { weekday: 'long' }).toLowerCase();
                                                        return (
                                                            <tr key={resource.id}>
                                                                <td>{resource.resourceName}</td>
                                                                <td>{resource.typeName}</td>
                                                                <td>
                                                                    <NumericInput
                                                                        min="0"
                                                                        value={resource[dayKey] || 0}
                                                                        onChange={(e) => this.handleQuantityChange(resource.id, e.target.value)}
                                                                    />
                                                                </td>
                                                            </tr>
                                                        );
                                                    })
                                            }
                                        </tbody>
                                    </Table>
                                </div>
                            </FormGroup>
                        </Col>
                    </Row>
                )}

                <FlexStartRow className="flex-wrap">
                    {/* Setup Charges */}
                    {contract?.setupChargesRequired && (
                        <div className="mr-2 mb-2">
                            <Button
                                size="sm"
                                color="primary"
                                onClick={() => this.setState({ showAddSetupCharge: true })}
                            >
                                <FontAwesomeIcon
                                    icon={faPlus}
                                    className="mr-2"
                                />
                                {' '}
                                Add Setup Charge
                            </Button>

                            {errors.setupCharge && (
                                <div>
                                    <small className="text-danger">
                                        {errors.setupCharge}
                                    </small>
                                </div>
                            )}
                        </div>
                    )}

                    {contract?.certificationRequired && (
                        <Button
                            size="sm"
                            color="primary"
                            onClick={() => this.setState({ showAddCertification: true })}
                            className="mr-2 mb-2"
                        >
                            <FontAwesomeIcon
                                icon={faPlus}
                                className="mr-2"
                            />
                            {' '}
                            Add Certification Requirement
                        </Button>
                    )}

                    {/* Permit Fees */}
                    {contract?.permitFeesApply && (
                        <FlexStartRow className="mb-2">
                            <FormCheckbox
                                id="permitFeesApply"
                                name="permitFeesApply"
                                checked={!!job.permitFeesApply}
                                onChange={this.handlePermitFeesApplyChange}
                                labelText="Permit Fees Apply"
                            />

                            {job?.permitFeesApply && (
                                <Button
                                    size="sm"
                                    color="secondary"
                                    onClick={() => this.setState({ showAddPermitFees: true })}
                                    className="ml-2"
                                >
                                    <FontAwesomeIcon
                                        icon={faPlus}
                                        className="mr-2"
                                    />
                                    {' '}
                                    Add Permit Fees...
                                </Button>
                            )}
                        </FlexStartRow>
                    )}
                </FlexStartRow>

                <ModalForm
                    isOpen={showAddSetupCharge}
                    onSubmit={() => this.onAddSetupCharge()}
                    toggle={() => this.setState({ showAddSetupCharge: false })}
                    title="Add Setup Charge"
                    SaveButtonProps={{
                        label: 'Close',
                        icon: faCheck,
                        outline: true,
                    }}
                    CancelButtonProps={{
                        hidden: true,
                    }}
                >
                    <table className="table table-sm table-bordered table-striped payroll-table">
                        <thead className="text-muted">
                            <tr>
                                <th style={{ width: '50px' }} />
                                <th className="invoiceHourlyCharge">
                                    Charge Name
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {!unusedSetupChargeTypes?.length ? (
                                <tr>
                                    <th className="invoiceHourlyCharge">
                                        Charge Name
                                    </th>
                                </tr>
                            ) : (

                                unusedSetupChargeTypes?.map(
                                    (
                                        charge,
                                    ) => {
                                        const id = `${charge.id}|${uuid()}`;

                                        return (
                                            <tr
                                                key={id}
                                            >
                                                <td className="text-center">

                                                    <input
                                                        id={id}
                                                        name={id}
                                                        type="checkbox"
                                                        checked={charge.isNew == true}
                                                        onChange={(x) => {
                                                            charge.isNew = x.currentTarget.checked;

                                                            this.setState({
                                                                charge,
                                                            });

                                                            hydrateSetupCharges();
                                                        }}
                                                    />

                                                </td>
                                                <td className="invoiceHourlyResource">
                                                    <label htmlFor={id}>
                                                        {charge.resourceName}
                                                    </label>
                                                </td>
                                            </tr>
                                        );
                                    },
                                )
                            )}
                        </tbody>
                    </table>

                    <div className="d-flex align-items-center justify-content-between mt-2">
                        <small className="text-muted">
                            {scTotal ? `${scSelected} of ${scTotal} selected` : 'No setup charges available'}
                        </small>
                        <Button
                            size="sm"
                            color="primary"
                            outline
                            disabled={!scTotal}
                            onClick={() => this.toggleAllSetupCharges(!scAll)}
                        >
                            {scCta}
                        </Button>
                    </div>
                </ModalForm>

                <ModalForm
                    isOpen={showAddPermitFees}
                    onSubmit={() => this.onAddPermitFees()}
                    toggle={() => this.setState({ showAddPermitFees: false })}
                    title="Add Permit Fees"
                    SaveButtonProps={{
                        label: 'Close',
                        icon: faCheck,
                        outline: true,
                    }}
                    CancelButtonProps={{
                        hidden: true,
                    }}
                >
                    <table className="table table-sm table-bordered table-striped payroll-table">
                        <thead className="text-muted">
                            <tr>
                                <th style={{ width: '50px' }} />
                                <th className="invoiceHourlyCharge">
                                    Charge Name
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {!unusedSetupChargeTypes?.length ? (
                                <tr>
                                    <td className="text-center">
                                        No Permit Fees Available
                                    </td>
                                </tr>
                            ) : (

                                unusedPermitFees?.map(
                                    (
                                        charge,
                                    ) => (
                                        <tr
                                            key={`${charge.id
                                                }|${uuid()}`}
                                        >
                                            <td className="text-center">

                                                <input
                                                    type="checkbox"
                                                    checked={charge.isNew == true}
                                                    onChange={(x) => {
                                                        charge.isNew = x.currentTarget.checked;

                                                        this.setState({
                                                            charge,
                                                        });

                                                        hydrateSetupCharges();
                                                    }}
                                                />

                                            </td>
                                            <td className="invoiceHourlyResource">
                                                {charge.resourceName}
                                            </td>

                                        </tr>
                                    ),
                                )
                            )}
                        </tbody>
                    </table>
                </ModalForm>

                <ModalForm
                    isOpen={showAddCertification}
                    onSubmit={() => this.onAddCertification()}
                    toggle={() => this.setState({ showAddCertification: false })}
                    title="Add Certification Requirement"
                    SaveButtonProps={{
                        label: 'Close',
                        icon: faCheck,
                        outline: true,
                    }}
                    CancelButtonProps={{
                        hidden: true,
                    }}
                >
                    <table className="table table-sm table-bordered table-striped payroll-table">
                        <thead className="text-muted">
                            <tr>
                                <th style={{ width: '50px' }} />
                                <th className="invoiceHourlyCharge">
                                    Certification Name
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            {!unusedCertifications?.length ? (
                                <tr>
                                    <td
                                        className="text-center"
                                    >
                                        No Certification Available
                                    </td>
                                </tr>
                            ) : (

                                unusedCertifications?.map(
                                    (
                                        charge,
                                    ) => {
                                        const id = `${charge.id}|${uuid()}`;

                                        return (
                                            <tr
                                                key={id}
                                            >
                                                <td className="text-center">
                                                    <input
                                                        id={id}
                                                        name={id}
                                                        type="checkbox"
                                                        checked={charge.isNew == true}
                                                        onChange={(x) => {
                                                            charge.isNew = x.currentTarget.checked;

                                                            this.setState({
                                                                charge,
                                                            });
                                                        }}
                                                    />

                                                </td>
                                                <td className="invoiceHourlyResource">
                                                    <label htmlFor={id}>
                                                        {charge.resourceName}
                                                    </label>
                                                </td>
                                            </tr>
                                        );
                                    },
                                )
                            )}
                        </tbody>
                    </table>
                </ModalForm>
            </FormContext.Provider>
        );
    }
}